import React, { useEffect, useMemo, useState } from 'react';
import { history } from 'umi';

import { Button, Card, Form, Icon, Input, Layout, Radio, RadioGroup, TextArea, notification, Switch } from 'tea-component';
import {useAigcIntl} from '@/pages/aigc/utils/utils';

import './index.scss';
import {editTool, getTagList} from '../Tools/service';
import TagDropdown from '../../components/TagDropdown';
import FormUpload from './components/FormUpload';
import { uploadSubmit } from './service';
import WangEditor from './components/WangEditor';
import useUploadForm from './useUploadForm';
const { Content } = Layout;

const ResourceUpload = () => {
  const { formatMessage, isEn } = useAigcIntl("upload");
  const state = (history?.location?.state && typeof history.location.state === 'string') ? JSON.parse(history.location.state) : {};

  const defaultTagValue = state?.tag_ids?.split(",")?.map((id: string) => Number(id)) || [];
  const [taglist, setTagList] = useState([]);
  const [name, setName] = useState(state?.name || '');
  const [website, setWebsite] = useState(state?.url || '');
  const [type, setType] = useState(state?.ai_tool_type || 'tool');
  const [toolValue, setToolValue] = useState(defaultTagValue);
  const [sceneValue, setSceneValue] = useState(defaultTagValue);
  const [description, setDescription] = useState(state?.description || '');
  const [imgUrl, setImgUrl] = useState(state?.cover || '');
  const [imgKey, setImgKey] = useState(1);
  const [content, setContent] = useState(state?.content || '');
  const [isInner, setIsInner] = useState(state?.is_inner_show !== undefined ? state?.is_inner_show : false);
  const [uploadStatus, setUploadStatus] = useState<string|undefined>(undefined);

  // 错误校验信息
  const {
    checkName,
    nameError,
    nameEnError,
    checkWebsite,
    websiteError,
    websiteEnError,
    checkType,
    typeError,
    typeEnError,
    checkApplication,
    applicationError,
    applicationEnError,
    checkDescription,
    descriptionError,
    descriptionEnError,
  } = useUploadForm({ name, website, toolValue, sceneValue, description });

  useEffect(() => {
    getTagList({ pagesize: 1000, offeset: 0, is_all_tag: true }).then((res) => {
      setTagList(res);
    });
  }, []);

  const toolOptions = useMemo(() => {
    const tool = taglist?.filter((item: any) => item?.side_tag_type === 'class');
    return tool?.map((item: any) => {
      return {
        text: isEn ? item?.tag_name_en : item?.tag_name,
        text_en: item?.tag_name_en,
        text_cn: item?.tag_name,
        value: item?.id
      };
    });
  }, [taglist, isEn]);

  const sceneOptions = useMemo(() => {
    const scene = taglist?.filter((item: any) => item?.side_tag_type === 'scene');
    return scene?.map((item: any) => {
      return {
        text: isEn ? item?.tag_name_en : item?.tag_name,
        text_en: item?.tag_name_en,
        text_cn: item?.tag_name,
        value: item?.id
      };
    });
  }, [taglist, isEn]);

  const handleSubmit = () => {
    if (!checkName() || !checkWebsite() || !checkType() || !checkApplication() || !checkDescription()) {
      return;
    } if (uploadStatus === "validating") {
      notification.warning({
        description: formatMessage('submit_warning'),
        duration: 1000,
      });
      return;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const tag_ids = Array.from(new Set([...toolValue, ...sceneValue]))?.join(',');
    const params = {
      name,
      url: website,
      ai_tool_type: type,
      tag_ids,
      description,
      attachment: imgUrl,
      content,
      id: state?.id,
      is_inner: isInner
    };
    if (state) {
      editTool(params).then(() => {
        notification.success({
          description: formatMessage('edit_success'),
          className: 'filter-lock-alert',
          duration: 300
        });
        history.push("AITools");
      }).catch(() => {
        notification.error({
          description: formatMessage('edit_error'),
          className: 'filter-lock-alert',
          duration: 500
        });
      });
    } else {
      uploadSubmit(params).then(() => {
        resetSubmit();
        notification.success({
          description: formatMessage('submit_success'),
          className: 'filter-lock-alert',
          duration: 500
        });
      }).catch(() => {
        notification.error({
          description: formatMessage('submit_error'),
          className: 'filter-lock-alert',
          duration: 500
        });
      });
    }
  };

  const resetSubmit = () => {
    setName('');
    setWebsite('');
    setType('tool');
    setToolValue([]);
    setSceneValue([]);
    setDescription('');
    setImgUrl('');
    setContent('');
    const time = new Date().getTime();
    setImgKey(time);
  };

  return <Content>
    <Content.Header
      title={
        <div className='aigc-upload-title'>
          <div
            className='aigc-upload-icon'
            onClick={() => history.goBack()}
          >
            <Icon type="btnback" />
          </div>
          {formatMessage(state ? "resource_edit" : "resource_upload")}
        </div>
      }
    />
    <Content.Body full className='aigc-upload'>
      <Card>
        <Card.Body>
          <Form className='aigc-upload-form'>
            <Form.Item
              label={formatMessage("name")}
              showStatusIcon={false}
              required={true}
              onBlur={() => { checkName() }}
              status={nameError ? 'error' : 'success'}
              message={isEn ? nameEnError : nameError}
            >
              <Input
                className='aigc-upload-input'
                placeholder={formatMessage("name_tips")}
                value={name}
                onChange={(value) => {
                  setName(value);
                }}
              />
            </Form.Item>
            <Form.Item
              label={formatMessage("website")}
              showStatusIcon={false}
              required={true}
              onBlur={() => { checkWebsite() }}
              status={websiteError ? 'error' : 'success'}
              message={isEn ? websiteEnError : websiteError}
            >
              <Input
                className='aigc-upload-input'
                placeholder={formatMessage("website_tips")}
                value={website}
                onChange={(value) => {
                  setWebsite(value);
                }}
              />
            </Form.Item>
            <Form.Item
              label={formatMessage("tool_case")}
              required={true}
              showStatusIcon={false}
            >
              <RadioGroup
                value={type}
                onChange={value => setType(value)}
              >
                <Radio name="tool">{formatMessage("tool")}</Radio>
                <Radio name="example">{formatMessage("case")}</Radio>
              </RadioGroup>
            </Form.Item>
            <Form.Item
              label={formatMessage("internal")}
              required={true}
              showStatusIcon={false}
            >
              <Switch value={isInner} onChange={value => setIsInner(value)}/>
            </Form.Item>
            <Form.Item
              label={formatMessage("type")}
              required={true}
              showStatusIcon={false}
              status={typeError ? 'error' : 'success'}
              message={isEn ? typeEnError : typeError}
            >
              <TagDropdown
                placeholder={formatMessage("type_tips")}
                options={toolOptions}
                tagValue={toolValue}
                setTagValue={setToolValue}
                check={checkType}
              />
            </Form.Item>
            <Form.Item
              label={formatMessage("application")}
              required={true}
              showStatusIcon={false}
              status={applicationError ? 'error' : 'success'}
              message={isEn ? applicationEnError : applicationError}
            >
              <TagDropdown
                placeholder={formatMessage("application_tips")}
                options={sceneOptions}
                tagValue={sceneValue}
                setTagValue={setSceneValue}
                check={checkApplication}
              />
            </Form.Item>
            <Form.Item
              label={formatMessage("description")}
              required={true}
              showStatusIcon={false}
              status={descriptionError ? 'error' : 'success'}
              message={isEn ? descriptionEnError : descriptionError}
            >
              <TextArea
                className='aigc-upload-input'
                value={description}
                placeholder={formatMessage("description_tips")}
                onChange={(value) => {
                  setDescription(value);
                }}
                onBlur={() => { checkDescription() }}
              />
            </Form.Item>
            <Form.Item
              className='aigc-upload-box'
              label={formatMessage("screenshot")}
            >
              <FormUpload
                key={imgKey}
                defaultInfo={
                  {
                    imgUrl: state?.cover,
                    createTime: state?.create_time
                  }
                }
                getUploadSuccessMessage={(uploadResMessage) => {
                  setImgUrl(uploadResMessage);
                }}
                getUploadStatus={(status) => setUploadStatus(status)}
              >
              </FormUpload>
            </Form.Item>
            <div className="tea-form__item aigc-upload-edit">
              <div className="tea-form__label"><label>{formatMessage("detailed_description")}</label></div>
              <WangEditor
                customStyle={{
                  minHeight: 250,
                  minWidth: 800,
                  maxWidth: 800,
                }}
                maxContentHeight={200}
                isEn={isEn}
                key={isEn ? 'en' : 'zh-CN'}
                defaultValue={content}
                placeholder={formatMessage("content")}
                status="success"
                errorTips="The amount of content exceeds the limit"
                autoFocus={false}
                getEditorContent={(v: string) => {
                  setContent(v);
                }}
                getEditorText={(_v: string) => {
                  // unused
                }}
                getIsFocused={(_v: boolean) => {
                  // unused
                }}
              >
              </WangEditor>
            </div>
          </Form>
          <hr></hr>
          <Button type="primary" onClick={handleSubmit}>
            {formatMessage("submit")}
          </Button>
        </Card.Body>
      </Card>
    </Content.Body>
  </Content>;
};

export default ResourceUpload;